import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import MessageTypeSelector from '../MessageTypeSelector';
import NodeSelector from '../NodeSelector';
import useOptionsGeneratedIds from 'src/presentation/components/pages/ivr/hooks/useOptionsGeneratedIds';
import {
  ContainerForOptions,
  Content,
  Footer,
  OptionContainer,
  Row,
  ScrollRow,
  StyledTextarea,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { NanoSecondsInputField } from 'src/presentation/components/pages/shared/NanoSecondsInputField';
import {
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { TWILIO_LANGUAGE_OPTIONS } from 'src/presentation/components/pages/ivr/constants/ivrLanguageConstants';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

type OptionComponentProps = {
  attrList: string;
  baseAttr: string;
  disabled?: boolean;
  index: number;
  nodeList: any;
  onSelectNode: Function;
  readOnly?: boolean;
  removeOptionIdByIndex: Function;
  selectedOptionIndex?: number;
};

const OptionComponent = ({
  attrList,
  baseAttr,
  disabled,
  index,
  nodeList,
  onSelectNode,
  readOnly,
  removeOptionIdByIndex,
  selectedOptionIndex,
}: OptionComponentProps) => {
  const dropdownAttr = `${baseAttr}.nextId`;

  return (
    <OptionContainer
      attrList={`${attrList}`}
      className="menu-item"
      currentIndex={index}
      data-aid={`menu[${index}]-item`}
      disabled={disabled}
      id={`menu[${index}]`}
      readOnly={readOnly}
      removeOptionIdByIndex={removeOptionIdByIndex}
      selectedIndex={selectedOptionIndex}
    >
      <Row>
        <InputField
          attr={`${baseAttr}.dtmf`}
          data-aid={`menu[${index}]-dial`}
          description="A number the customer can input to go to the next node"
          disabled={disabled}
          label="DTMF Input"
          readOnly={readOnly}
        />
      </Row>

      <Row>
        <StyledTextarea
          attr={`${baseAttr}.speech`}
          data-aid={`menu[${index}].speech`}
          description="A set of phrases or words a customer customer can say instead of pressing the keypad to go to the next node. You can separate the list of words with commas or use a regular expression."
          disabled={disabled}
          label="Voice Input Phrases"
          placeholder="Please use a paragraph input field"
          readOnly={readOnly}
        />
      </Row>

      <Row>
        <NodeSelector
          attr={dropdownAttr}
          data-aid={`menu[${index}].nextId`}
          description="The next node in the IVR"
          disabled={disabled}
          label="Next Node"
          onSelectNode={onSelectNode}
          options={nodeList}
          readOnly={readOnly}
          searchable
        />
      </Row>
    </OptionContainer>
  );
};

const MenuNode: FC<NodeProps> = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }) => {
  const { updateState, workingState } = useContext(FormContext);
  const [selectedIndex, setSelectedIndex] = useState(selectedOptionIndex);

  const isFeatureEnabled = useIsFeatureEnabled();
  const isTwilioGatherV2Enabled = isFeatureEnabled('twilioGatherV2');

  useEffect(() => {
    setSelectedIndex(selectedOptionIndex);
  }, [selectedOptionIndex, setSelectedIndex]);

  const currentNodeId = get(workingState, `${attr}.id`);
  const defaultNextNodeAttr = `${attr}.badSelectionNodeId`;
  const menuItems = get(workingState, `${attr}.menu`, []);
  const omittedNodes = isEmpty(menuItems) ? [currentNodeId] : [];

  const { addOptionId, optionsIds, removeOptionIdByIndex } = useOptionsGeneratedIds(menuItems);

  const onAddButtonClick = useCallback(() => {
    const newMenuItem = [
      ...menuItems,
      {
        dtmf: '',
        speech: '',
        nextId: null,
      },
    ];
    updateState(set({}, `${attr}.menu`, newMenuItem));
    setSelectedIndex(newMenuItem.length - 1);
    addOptionId();
  }, [attr, menuItems, setSelectedIndex, updateState, addOptionId]);

  const renderedMenuItems = (
    <ContainerForOptions
      addButtonLabel="Add Menu Item"
      onAddButtonClick={onAddButtonClick}
      disabled={disabled || readOnly}
      label=""
      context={map(menuItems, (_, idx: number) => {
        const baseAttr = `${attr}.menu[${idx}]`;
        return (
          <OptionComponent
            attrList={`${attr}.menu`}
            baseAttr={baseAttr}
            disabled={disabled}
            index={idx}
            key={optionsIds[idx]}
            nodeList={nodeList}
            onSelectNode={onSelectNode}
            readOnly={readOnly}
            removeOptionIdByIndex={removeOptionIdByIndex}
            selectedOptionIndex={selectedIndex}
          />
        );
      })}
    />
  );

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid="name"
            disabled={disabled}
            label="Name"
            description="The name you can refer to link to other nodes"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid="description"
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <Dropdown
            attr={`${attr}.language`}
            data-aid="language"
            description="The language we will try to understand as the caller is speaking"
            disabled={disabled}
            label="Language"
            options={TWILIO_LANGUAGE_OPTIONS}
            readOnly={readOnly}
            searchable
            placeholder="English (United States)"
          />
        </Row>

        <Row>
          <NanoSecondsInputField
            attr={`${attr}.speechTimeout`}
            data-aid="speechTimeout"
            description="Time in seconds to wait for the caller to press another digit or say another word before the message repeats itself"
            disabled={disabled}
            label={isTwilioGatherV2Enabled ? 'Timeout' : 'Speech Timeout'}
            readOnly={readOnly}
          />
        </Row>

        <MessageTypeSelector attr={attr} readOnly={readOnly} disabled={disabled} />

        {renderedMenuItems}
      </Content>
      <Footer>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={defaultNextNodeAttr}
            data-aid="badSelectionNodeId"
            disabled={disabled}
            label="Default Next Node"
            description="The next IVR node if none of the menu items are matched"
            omittedNodeIds={omittedNodes}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default MenuNode;
